import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import Logo from '../../components/Logo/Logo';

const markdownComponents = {
  p: ({ node, ...props }) => (
    <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-3" {...props} />
  ),
  ul: ({ node, ...props }) => (
    <ul className="list-disc pl-5 space-y-1.5 mb-3 text-sm sm:text-base text-muted-foreground" {...props} />
  ),
  ol: ({ node, ...props }) => (
    <ol className="list-decimal pl-5 space-y-1.5 mb-3 text-sm sm:text-base text-muted-foreground" {...props} />
  ),
  li: ({ node, ...props }) => <li className="leading-relaxed" {...props} />,
  strong: ({ node, ...props }) => <strong className="font-semibold text-foreground" {...props} />,
  a: ({ node, children, ...props }) => (
    <a className="text-primary font-medium hover:underline break-words" {...props}>
      {children}
    </a>
  ),
};

const LegalDocument = ({ data, relatedLinks = [] }) => {
  const { t } = useTranslation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background pt-4 sm:pt-6">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between mb-6">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Home
          </Link>
          <Link to="/" aria-label={t('go_to_home', 'Go to home')} className="hover:opacity-80 transition-opacity">
            <Logo className="w-9 h-9 sm:w-10 sm:h-10" />
          </Link>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-white rounded-2xl border border-border shadow-sm p-5 sm:p-8"
        >
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground text-center">{data.title}</h1>
          {data.effective && (
            <p className="text-xs sm:text-sm text-muted-foreground text-center mt-2">{data.effective}</p>
          )}
          {data.subtitle && (
            <p className="text-xs text-muted-foreground/80 text-center mt-1">{data.subtitle}</p>
          )}
          {data.note && (
            <p className="text-xs text-primary text-center mt-1">{data.note}</p>
          )}

          {relatedLinks.length > 0 && (
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 mt-5">
              {relatedLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="text-sm font-semibold text-primary hover:underline"
                >
                  {link.label} →
                </Link>
              ))}
            </div>
          )}

          {data.intro && (
            <div className="mt-6 pt-6 border-t border-border">
              <ReactMarkdown components={markdownComponents}>{data.intro}</ReactMarkdown>
            </div>
          )}

          <div className="mt-6 space-y-7">
            {data.sections.map((section, index) => (
              <section key={index}>
                <h2 className="text-lg sm:text-xl font-semibold text-foreground mb-2">{section.title}</h2>
                <ReactMarkdown components={markdownComponents}>{section.content}</ReactMarkdown>
              </section>
            ))}
          </div>

          {data.closing && (
            <p className="mt-8 pt-5 border-t border-border text-xs sm:text-sm text-muted-foreground whitespace-pre-line">
              {data.closing}
            </p>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default LegalDocument;
